// Formatadores compartilhados entre todos os componentes de auditoria.
// formatCurrency não usa { style: 'currency' } do Intl para evitar o caractere genérico ¤
// que aparece quando o runtime (Node.js / browser) não tem ICU completo para BRL.
// PREFIXO_MOEDA usa espaço NÃO SEPARÁVEL (U+00A0/NBSP) entre "R$" e o valor de propósito, para
// "R$" nunca quebrar de linha separado do número — reaproveitado por formatCurrencyCompacto
// abaixo para manter a mesma convenção tipográfica em todo lugar que exibe moeda.
const PREFIXO_MOEDA = 'R$ ';

export const formatCurrency = (value?: number): string => {
  const n = value ?? 0;
  try {
    return PREFIXO_MOEDA + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return PREFIXO_MOEDA + n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
};

// COR-186: versão compacta de formatCurrency para caber dentro do gauge circular do cockpit
// (o texto do gauge é um <text> SVG de uma linha só, sem quebra — um valor longo como
// "R$ 357.790,82" transbordava do círculo). Mil/milhão abreviados, igual ao mock aprovado
// ("R$ 357,8 mil de R$ 500 mil").
export const formatCurrencyCompacto = (value?: number): string => {
  const n = value ?? 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return PREFIXO_MOEDA + (n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' mi';
  if (abs >= 1_000) return PREFIXO_MOEDA + (n / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' mil';
  return formatCurrency(n);
};

// COR-188: versão SEM o prefixo "R$" de formatCurrencyCompacto, para o texto de dentro do gauge
// do cockpit — mesmo com abreviação (mil/mi), "R$ 467,8 mil" ainda ocupava 2 linhas dentro do
// círculo; sem o "R$" (que já aparece no "de X" logo abaixo e no tooltip com o valor completo),
// o texto cabe numa linha só. Usado só para o número central do gauge — nunca para exibição
// avulsa de moeda em outro lugar da tela.
export const formatCurrencyGauge = (value?: number): string => {
  const n = value ?? 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' mi';
  if (abs >= 1_000) return (n / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' mil';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// formatDate com guarda contra datas inválidas (NaN) e contra DateTime.MinValue do C#
// (0001-01-01T00:00:00 é um Date JS válido — ano 1 — mas nunca é uma data de negócio real).
export const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  if (date.getFullYear() < 1900) return '-';
  return date.toLocaleDateString('pt-BR');
};
